// ─── batch-55 inkoop-correctiemail (FO-420) ──────────────────────────────────
//
// Correctie-mail naar de relatie wanneer een inkoopfactuur op een OUDE BV-naam
// (alias, bv. "Leander Techniek" -> STREEP Personeel) is gericht, met als vaste
// bijlage het per BV geüploade bedrijfsgegevens-document. Gedeelde DTO's,
// constanten en het schrijf-schema (client én server valideren hiermee).
//
// Datamodel: EntityBedrijfsgegevensDoc + InkoopCorrectieMailVerzending
// (packages/db/prisma/schema.prisma, sectie "batch-55 inkoop-correctiemail").

import { z } from 'zod'

/**
 * Maximale grootte van de per-BV bedrijfsgegevens-PDF. Gelijk aan de
 * per-bijlage-limiet van de MailService (4 MB): een groter document zou
 * uploaden maar nooit verstuurd kunnen worden, dus we weigeren het aan de bron.
 */
export const CORRECTIE_MAIL_BIJLAGE_MAX_MB = 4
export const CORRECTIE_MAIL_BIJLAGE_MAX_BYTES = CORRECTIE_MAIL_BIJLAGE_MAX_MB * 1024 * 1024

/**
 * Venster (dagen) waarbinnen een relatie als "recent al geïnformeerd" geldt.
 * Alleen een signaal in de actie; opnieuw versturen blijft altijd mogelijk.
 */
export const CORRECTIE_MAIL_RECENT_DAGEN = 90

/**
 * OutgoingEmail-statussen waarbij de mail aantoonbaar NIET bij de relatie is
 * aangekomen (kill-switch/allowlist, definitief mislukt, gebounced). Zo'n
 * verzending telt niet als "relatie geïnformeerd" (kaart, badge, recent-
 * signaal) en de audit benoemt het expliciet; de poging blijft wél in de
 * historie staan, mét status.
 */
export const CORRECTIE_MAIL_NIET_BEZORGD_STATUSSEN = ['DROPPED_ALLOWLIST', 'FAILED', 'BOUNCED'] as const

export function isCorrectieMailNietBezorgd(status: string | null | undefined): boolean {
  return (
    status != null && (CORRECTIE_MAIL_NIET_BEZORGD_STATUSSEN as readonly string[]).includes(status)
  )
}

/** Waar een voorgesteld ontvangstadres vandaan komt (weergave in de actie). */
export const CORRECTIE_MAIL_ADRES_BRON = ['FACTUURMAIL', 'RELATIE', 'CONTACTPERSOON'] as const
export type CorrectieMailAdresBron = (typeof CORRECTIE_MAIL_ADRES_BRON)[number]

export const CORRECTIE_MAIL_ADRES_BRON_LABELS: Record<CorrectieMailAdresBron, string> = {
  FACTUURMAIL: 'afzender van de factuurmail',
  RELATIE: 'e-mailadres van de relatie',
  CONTACTPERSOON: 'contactpersoon van de relatie',
}

/**
 * Waarom een gevonden adres NIET als standaardadres wordt voorgesteld. Het
 * blijft handmatig kiesbaar (de gebruiker ziet de reden), maar staat achteraan.
 */
export const CORRECTIE_MAIL_ADRES_AFGERADEN = ['NO_REPLY', 'EIGEN_DOMEIN'] as const
export type CorrectieMailAdresAfgeraden = (typeof CORRECTIE_MAIL_ADRES_AFGERADEN)[number]

export const CORRECTIE_MAIL_ADRES_AFGERADEN_LABELS: Record<CorrectieMailAdresAfgeraden, string> = {
  NO_REPLY: 'no-reply-adres: antwoorden komen niet aan',
  EIGEN_DOMEIN: 'eigen STREEP-adres: waarschijnlijk een doorgestuurde factuur',
}

/** Foutcodes van de correctie-mail-endpoints (naast de generieke codes). */
export const CORRECTIE_MAIL_FOUT_CODES = {
  /** Geen bedrijfsgegevens-document voor de BV: er wordt NIETS verstuurd. */
  GEEN_BIJLAGE: 'CORRECTIE_MAIL_GEEN_BIJLAGE',
  /** Factuur is niet via een oude naam herkend (of heeft geen BV): geen actie. */
  NIET_VAN_TOEPASSING: 'CORRECTIE_MAIL_NIET_VAN_TOEPASSING',
} as const

/** Het actieve bedrijfsgegevens-document van een BV (instellingen + mail-bijlage). */
export interface EntityBedrijfsgegevensDocDTO {
  id: string
  entityId: string
  bestandsnaam: string
  bestandsgrootte: number
  geuploadOp: string
  geuploadDoorNaam: string | null
}

/** Eén vastgelegde verzending (poging) van de correctie-mail (verzendlog). */
export interface InkoopCorrectieMailVerzendingDTO {
  id: string
  factuurId: string
  factuurNr: string | null
  entityId: string
  /** BV-naam op het verzendmoment (momentopname uit het log). */
  entityNaam: string
  oudeNaam: string
  nieuweNaam: string
  ontvangerAdres: string
  verzondenOp: string
  verzondenDoorNaam: string | null
  /** Live status uit de OutgoingEmail-tracering (null = rij niet (meer) gevonden). */
  mailStatus: string | null
}

/**
 * Compact blok op het inkoopfactuur-detail. Alleen gevuld wanneer de factuur
 * gericht is aan een oude naam (alias) van de gekoppelde BV; null = geen actie
 * (factuur op de juiste naam, geen BV, of geen geadresseerde naam bekend).
 */
export interface InkoopNaamswijzigingDTO {
  /** De alias (oude naam) die op de factuur herkend is. */
  oudeNaam: string
  /** De juiste, huidige naam van de BV. */
  nieuweNaam: string
  /** De geadresseerde naam exact zoals op de factuur stond. */
  factuurNaam: string
  /**
   * Laatste verzending naar deze relatie VOOR DEZE BV die niet aantoonbaar
   * mislukt is (zie CORRECTIE_MAIL_NIET_BEZORGD_STATUSSEN). null = de relatie
   * is voor deze BV nog niet (succesvol) geïnformeerd.
   */
  laatsteVerzending: InkoopCorrectieMailVerzendingDTO | null
  /** true als `laatsteVerzending` binnen CORRECTIE_MAIL_RECENT_DAGEN valt. */
  recentGeinformeerd: boolean
}

export interface InkoopCorrectieMailAdresSuggestieDTO {
  adres: string
  bron: CorrectieMailAdresBron
  /** null = bruikbaar als standaardadres; anders de reden waarom hij alleen bewust kiesbaar is. */
  afgeraden: CorrectieMailAdresAfgeraden | null
}

/** Voorvertoning van de correctie-mail (GET) vóór de bewuste verzending. */
export interface InkoopCorrectieMailPreviewDTO {
  factuurId: string
  factuurNr: string
  entity: { id: string; name: string }
  leverancier: { id: string; name: string }
  oudeNaam: string
  nieuweNaam: string
  factuurNaam: string
  onderwerp: string
  /** Platte tekst van de mail zoals verstuurd (de HTML-variant krijgt het STREEP-mailframe). */
  bodyText: string
  /** null = geen document geüpload voor deze BV: versturen is dan geblokkeerd. */
  bijlage: EntityBedrijfsgegevensDocDTO | null
  /**
   * Vooringevuld adres: de eerste niet-afgeraden suggestie. null = niets
   * bruikbaars afleidbaar (handmatig invullen, of bewust een afgeraden adres kiezen).
   */
  standaardAdres: string | null
  /** Gevonden adressen, bruikbare eerst, afgeraden (no-reply/eigen domein) achteraan. */
  adresSuggesties: InkoopCorrectieMailAdresSuggestieDTO[]
  /** Voor deze relatie én deze BV (zie InkoopNaamswijzigingDTO). */
  recentGeinformeerd: boolean
  laatsteVerzending: InkoopCorrectieMailVerzendingDTO | null
  /** Alle pogingen naar deze relatie (alle BV's, ook mislukte, met status; nieuwste eerst, begrensd). */
  verzendingen: InkoopCorrectieMailVerzendingDTO[]
  /** Hoeveel pogingen er in totaal zijn (voor een eerlijk "N van M"-signaal). */
  verzendingenTotaal: number
}

/** Schrijf-schema van de verzend-actie: het (eventueel aangepaste) ontvangstadres. */
export const inkoopCorrectieMailVerzendSchema = z.object({
  ontvangerAdres: z
    .string()
    .trim()
    .min(1, 'Vul een e-mailadres in')
    .email('Ongeldig e-mailadres'),
})
export type InkoopCorrectieMailVerzendInput = z.infer<typeof inkoopCorrectieMailVerzendSchema>

export interface InkoopCorrectieMailVerzendResultaatDTO {
  verzending: InkoopCorrectieMailVerzendingDTO
  outgoingEmailId: string
  /** Status van de OutgoingEmail direct na het aanbieden (QUEUED, of DROPPED_ALLOWLIST buiten productie). */
  mailStatus: string
}
